import { useQueries } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import type { ValueOf } from 'type-fest';

import type { PerioChartResponse } from '@/common-components/composed/PerioChart/types';
import type { SeverityColor } from '@/utils/hooks/useRblSeverities';

import type { TDisplayOrderQuadrantAbbreviations } from '../modules/patientList/patientStrip/patientStripOdontogram/types';

import { makeAPIRequestReactQuery } from './api';
import { endpoints } from './endpoints';

export const useVisitSummaries = (
  args?: TVisitSummaryArgs,
  onSuccess?: (data: TVisitSummary[]) => void,
  options?: { refetchInterval?: number | undefined },
) => {
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector(
    (state: {
      login: {
        user: {
          primaryPracticeId?: string;
          primaryOrganizationId?: string;
        };
      };
    }) => state.login.user,
  );

  const practiceId = args?.practiceId || currentPractice;

  const visitsLength = args?.visits?.length || 0;
  const groupedVisits: TVisitSummaryVisitsArgs[][] = [];
  for (let i = 0; i < visitsLength; i += 10) {
    const firstIndex = i;
    const lastIndex = i + 10 < visitsLength ? i + 10 : visitsLength;
    groupedVisits.push(args?.visits?.slice(firstIndex, lastIndex) || []);
  }

  return useQueries({
    queries: groupedVisits.length
      ? groupedVisits.map((visits) => {
          return {
            queryKey: [endpoints.visitSummaries, visits, practiceId],
            staleTime: 10 * 1000,
            refetchInterval: options?.refetchInterval,
            queryFn: async () => {
              if (!visits?.length) {
                return [];
              }
              const resp = await makeAPIRequestReactQuery<TVisitSummary[]>(
                endpoints.visitSummaries,
                undefined,
                'post',
                {
                  headers: {
                    practice: practiceId,
                    org: currentOrg,
                  },
                  data: {
                    visits,
                  },
                },
              );
              onSuccess?.(resp);
              return resp;
            },
            enabled: Boolean(visits?.length && practiceId && currentOrg),
          };
        })
      : [],
  });
};
export const ToothProcedureNames = {
  crown: 'crown',
  enamelRegen: 'enamelRegen',
  extraction: 'extraction',
  filling: 'filling',
  implant: 'implant',
  other: 'other',
  pulpotomy: 'pulpotomy',
  rootCanal: 'rootCanal',
} as const;

export type TToothProcedureNames = ValueOf<typeof ToothProcedureNames>;

export const QuadProcedureNames = {
  perio: 'perio',
  locPerio: 'locPerio',
} as const;
export type TQuadProcedureNames = ValueOf<typeof QuadProcedureNames>;

export const ArchProcedureNames = {
  aligner: 'aligner',
} as const;
export type TArchProcedureNames = ValueOf<typeof ArchProcedureNames>;

export const ProcedureStatus = {
  completed: 'completed',
  scheduled_future: 'scheduled_future',
  scheduled_today: 'scheduled_today',
  unscheduled: 'unscheduled',
  recommended: 'recommended',
  normal: 'normal',
} as const;

export type TProcedure = {
  name:
    | TToothProcedureNames
    | TQuadProcedureNames
    | TArchProcedureNames
    | 'other';
  priority: number;
  recommended: boolean;
  status: TProcedureStatus;
  treatments: {
    scheduledToday: TVisitTreatment[];
    completed: TVisitTreatment[];
    scheduledFuture: TVisitTreatment[];
    unscheduled: TVisitTreatment[];
  };
};

export type TProcedureStatus = ValueOf<typeof ProcedureStatus>;

export type TVisitSummaryVisitsArgs = {
  unifiedAppointmentId?: number;
  estimatedVisitId?: number | null | undefined;
};
export type TVisitSummaryArgs =
  | {
      visits?: TVisitSummaryVisitsArgs[] | null;
      practiceId?: string;
    }
  | undefined;

export interface TVisitTreatment {
  priority?: number;
  status: TProcedureStatus;
  toothId: string;
  txCode: string;
  txDate: string | null;
  txDescription: string;
  txGroupDescription: string;
  txFeatures?: {
    correlation: number;
    name: TreatmentGroupFeatureName;
  }[];
  /**
   * use this functionally
   * for functional - @see txGroupLongName
   */
  txGroupShortName: string;
  /**
   * use this for display
   * for functional - @see txGroupShortName
   */
  txGroupLongName: Record<string, string>;
  /**
   * this is the visualization for the treatment group
   * this is the same type as txGroupShortName
   */
  txGroupVisualization?: TTreatmentGroupShortNames | null;
  txId: number;
  txScope?: string;
  txAffectedTeeth?: string;
}

export const ToothSvgTypes = {
  ...ToothProcedureNames,
  missing: 'missing',
  normal: 'normal',
  extracted: 'extracted',
  rootCanalAndCrown: 'rootCanalAndCrown',
} as const;

export function isToothProcedureName(
  name: string,
): name is TToothProcedureNames {
  return Object.values(ToothProcedureNames).includes(
    name as TToothProcedureNames,
  );
}

export type TToothSvgTypes = ValueOf<typeof ToothSvgTypes>;

export const QuadrantType = {
  perio: 'perio',
  normal: 'normal',
} as const;

export type TQuadrantType = ValueOf<typeof QuadrantType>;
export interface TVisitSummary {
  estimatedVisitId: number | null | undefined;
  unifiedAppointmentId: number | null | undefined;
  hasDentalPhotos?: boolean;
  isPerioPatient?: boolean;
  perioChart?: PerioChartResponse;
  mouth: {
    procedures: TProcedure[];
    treatments: TVisitTreatment[] | undefined;
    aiOverview: {
      calc: boolean;
    };
    /** Most severe perio stage (1-4) across all quadrants */
    stage?: number;
  };
  arches: {
    arch: 'maxillary' | 'mandibular';
    procedures: TProcedure[];
    treatments?: TVisitTreatment[] | undefined;
    hasCrowding?: boolean;
    summedToothSpacing?: number;
  }[];
  quadrants: {
    potentialSrp?: boolean;
    potentialLocSrp?: boolean;
    srpImageManifestIds: number[];
    treatments?: TVisitTreatment[] | undefined;
    quadrant: TDisplayOrderQuadrantAbbreviations;
    quadrantType: TQuadrantType;
    procedures: TProcedure[];
    rblSeverities?: {
      green?: number;
      yellow?: number;
      orange?: number;
      red?: number;
    };
    aiOverview: {
      perio: boolean;
    };
    /** Perio stage (1-4) for this quadrant */
    stage?: number;
  }[];
  teeth: {
    /**
     * this is the universal tooth id
     */
    toothId: string;
    dentition?: string | null | undefined;
    /**
     * this means we captured the space where the tooth should be, but there was no tooth here.
     * this is different than notDetected.
     * this is different than definitely extracted (which is informed by past treatment data)
     */
    isMissing: boolean;
    hasAnalysis?: boolean;
    hasSummary?: boolean;
    treatments?: TVisitTreatment[] | undefined;
    potentialCrown?: boolean;
    potentialCrownImageIds?: number[];
    potentialEnamelRegen?: boolean;
    potentialIncipientImageId?: number;
    procedures: TProcedure[];
    aiFindings: string[];
    aiOverview: {
      resto: boolean;
      endo: boolean;
      perio: boolean;
      calc: boolean;
    };
    gapDist?: number;
    dmf?: number;
    boneLevelDistal?: number;
    boneLevelMesial?: number;
    boneLevelSourceImageType?: 'BITEWING' | 'PERIAPICAL';
    paDistalBoneLevel?: number;
    paMesialBoneLevel?: number;
    distBwAblMesialToCejMesial?: number;
    distBwAblDistalToCejDistal?: number;
    rblSeverity?: SeverityColor;
  }[];
}

export const TreatmentGroupShortNames = {
  aligners: 'aligners',
  bitewings: 'bitewings',
  bitewingsSingle: 'bitewingsSingle',
  bridges: 'bridges',
  buildupsPinsPostsCore: 'buildupsPinsPostsCore',
  cbct: 'cbct',
  crown: 'crown',
  curodont: 'curodont',
  dentures: 'dentures',
  enamelRegen: 'enamelRegen',
  exam: 'exam',
  extraction: 'extraction',
  filling: 'filling',
  fmx: 'fmx',
  implantAbutments: 'implantAbutments',
  implantBridges: 'implantBridges',
  implantCrowns: 'implantCrowns',
  implantDentures: 'implantDentures',
  implants: 'implants',
  inlay: 'inlay',
  inlayOnlay: 'inlayOnlay',
  minimallyInvasive: 'minimallyInvasive',
  onlay: 'onlay',
  ortho: 'ortho',
  perioChart: 'perioChart',
  perioMaintenance: 'perioMaintenance',
  perioServices: 'perioServices',
  polishing: 'polishing',
  prophy: 'prophy',
  resinInfiltration: 'resinInfiltration',
  rootCanal: 'rootCanal',
  rootPlaning: 'rootPlaning',
  scaling: 'scaling',
  sealants: 'sealants',
  srp: 'srp',
  veneers: 'veneers',
} as const;

export type TTreatmentGroupShortNames = ValueOf<
  typeof TreatmentGroupShortNames
>;

const TreatmentGroupFeatureNames = [
  'alignerIndicator',
  'crownIndicator',
  'displayedTreatments',
  'incipientIndicator',
  'extractionIndicator',
  'fillingIndicator',
  'implantIndicator',
  'perioIndicator',
  'rootCanalIndicator',
] as const;
type TreatmentGroupFeatureName = (typeof TreatmentGroupFeatureNames)[number];

export const RecommendationToFeatureMap: Record<
  Exclude<TProcedure['name'], 'other'>,
  TreatmentGroupFeatureName
> = {
  aligner: 'alignerIndicator',
  crown: 'crownIndicator',
  enamelRegen: 'incipientIndicator',
  extraction: 'extractionIndicator',
  filling: 'fillingIndicator',
  implant: 'implantIndicator',
  locPerio: 'perioIndicator',
  perio: 'perioIndicator',
  pulpotomy: 'rootCanalIndicator',
  rootCanal: 'rootCanalIndicator',
};
